// ─── Demo Mode Data ─────────────────────────────────────────────────────────
// Pre-built realistic data for the "Try Demo" experience.
// No Supabase queries — everything is returned from here.

import { generateMockMetrics } from './dashboardHelpers';
import { gradePhenotypes, PhenotypeInput } from './phenotype';
import type { DailyMetrics, PHQ4Entry, WeeklyCheckinEntry, UserProfile, LabEntry, LabPanel } from '../types';
import type { AppleExtras, DashboardData } from '../hooks/useDashboardData';

// ─── Profile ────────────────────────────────────────────────────────────────

export const DEMO_PROFILE: UserProfile = {
  id: 'demo-user-000',
  email: 'demo@aretehealth.com',
  full_name: 'Alex Demo',
  dob: '1990-06-15',
  sex: 'male',
  oura_token: null,
  role: 'patient',
  created_at: '2025-12-01T00:00:00Z',
};

// ─── Wearable metrics (90 days, with bedtime_start) ──────────────────────────

/** Deterministic pseudo-random for stable demo data across renders. */
function seededRandom(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 49297;
  return x - Math.floor(x);
}

function addBedtimeStart(metrics: DailyMetrics[]): DailyMetrics[] {
  return metrics.map((m, i) => {
    // Generate realistic bedtime: 10:00 PM – 11:30 PM with variation
    // Include 2-3 "late nights" per month for interesting circadian Z-scores
    const baseHour = 22;
    const isLateNight = i % 15 === 7 || i % 20 === 13; // ~2 late nights per month
    const jitterRange = isLateNight ? 120 : 75; // late nights: up to +2h shift
    const jitter = Math.floor(seededRandom(i + 42) * jitterRange) - 15;
    const bt = new Date(m.date + 'T00:00:00');
    bt.setHours(baseHour, jitter, 0, 0);
    return { ...m, bedtime_start: bt.toISOString() };
  });
}

export const DEMO_METRICS_90: DailyMetrics[] = addBedtimeStart(generateMockMetrics(90));
export const DEMO_METRICS: DailyMetrics[] = DEMO_METRICS_90.slice(-30);

// ─── Apple Health extras ────────────────────────────────────────────────────

export const DEMO_APPLE_EXTRAS: AppleExtras = {
  restingHR: 62,
  spo2: 97,
  distance: 4.2,
  weight: 172.5,
  weightDelta: -1.3,
  activeCalories: 385,
  daylight: 45,
  morningLight: { firstTimeStr: '7:42 AM', isMorning: true },
  bmi: 24.8,
  bmiDelta: -0.2,
  bodyFat: 22.3,
  bodyFatDelta: -0.5,
  vo2Max: 36.2,
  vo2MaxDelta: 0.8,
};

// ─── PHQ-4 entries (8 weeks, mild improvement) ──────────────────────────────

function buildPhq4Entries(): PHQ4Entry[] {
  const entries: PHQ4Entry[] = [];
  const now = new Date();
  // Scores trend: 8, 7, 7, 6, 6, 5, 5, 4 (mild improvement over 8 weeks)
  const scores = [
    { q1: 2, q2: 2, q3: 2, q4: 2 }, // week -8: score 8
    { q1: 2, q2: 2, q3: 2, q4: 1 }, // week -7: score 7
    { q1: 2, q2: 2, q3: 1, q4: 2 }, // week -6: score 7
    { q1: 2, q2: 1, q3: 2, q4: 1 }, // week -5: score 6
    { q1: 1, q2: 2, q3: 2, q4: 1 }, // week -4: score 6
    { q1: 1, q2: 1, q3: 2, q4: 1 }, // week -3: score 5
    { q1: 1, q2: 2, q3: 1, q4: 1 }, // week -2: score 5
    { q1: 1, q2: 1, q3: 1, q4: 1 }, // week -1: score 4
  ];
  for (let i = 0; i < scores.length; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() - (scores.length - 1 - i) * 7);
    const s = scores[i];
    entries.push({
      id: `demo-phq4-${i}`,
      user_id: DEMO_PROFILE.id,
      week: `${d.getFullYear()}-W${String(Math.ceil((d.getTime() - new Date(d.getFullYear(), 0, 1).getTime()) / 604800000)).padStart(2, '0')}`,
      q1: s.q1, q2: s.q2, q3: s.q3, q4: s.q4,
      score: s.q1 + s.q2 + s.q3 + s.q4,
      created_at: d.toISOString(),
    });
  }
  return entries;
}

export const DEMO_PHQ4_ENTRIES = buildPhq4Entries();
export const DEMO_PHQ4_LATEST = DEMO_PHQ4_ENTRIES[DEMO_PHQ4_ENTRIES.length - 1];

// ─── Weekly check-in ────────────────────────────────────────────────────────

export const DEMO_CHECKIN: WeeklyCheckinEntry = {
  id: 'demo-checkin-001',
  user_id: DEMO_PROFILE.id,
  week: DEMO_PHQ4_LATEST.week,
  diet: 2,           // Good
  appetite: 2,       // Normal
  stress: 1,         // Struggling
  substances: 3,     // None/Min
  social: 2,         // Connected
  symptoms: 'Fatigue, Brain fog, Poor sleep',
  notes: null,
  rum_q1: 2, rum_q2: 2, rum_q3: 1,   // Moderate rumination (score 5/9)
  bads_q1: 1, bads_q2: 1, bads_q3: 2, // Low avoidance, moderate activation
  hint_q1: 1, hint_q2: 1, hint_q3: 1, // Mild negative thinking (score 3/9)
  created_at: DEMO_PHQ4_LATEST.created_at,
};

// ─── Key labs (mix of optimal, borderline, out-of-range) ────────────────────

export const DEMO_KEY_LABS: DashboardData['keyLabs'] = [
  { marker: 'Vitamin D', value: 32, unit: 'ng/mL', ref_low: 30, ref_high: 100, panel: 'other', date: '2026-02-15' },
  { marker: 'hsCRP', value: 2.1, unit: 'mg/L', ref_low: 0, ref_high: 3, panel: 'metabolic', date: '2026-02-15' },
  { marker: 'Vitamin B12', value: 650, unit: 'pg/mL', ref_low: 200, ref_high: 1100, panel: 'other', date: '2026-02-15' },
  { marker: 'Magnesium', value: 2.1, unit: 'mg/dL', ref_low: 1.7, ref_high: 2.4, panel: 'metabolic', date: '2026-02-15' },
  { marker: 'TSH', value: 2.3, unit: 'mIU/L', ref_low: 0.4, ref_high: 4.0, panel: 'thyroid', date: '2026-02-15' },
  { marker: 'Ferritin', value: 45, unit: 'ng/mL', ref_low: 15, ref_high: 200, panel: 'cbc', date: '2026-02-15' },
  { marker: 'Omega-3 Index', value: 5.2, unit: '%', ref_low: 4, ref_high: 12, panel: 'other', date: '2026-02-15' },
  { marker: 'Insulin', value: 9.5, unit: 'uIU/mL', ref_low: 2, ref_high: 25, panel: 'metabolic', date: '2026-02-15' },
  { marker: 'Homocysteine', value: 11, unit: 'umol/L', ref_low: 4, ref_high: 15, panel: 'other', date: '2026-02-15' },
  { marker: 'Cortisol', value: 19, unit: 'ug/dL', ref_low: 6, ref_high: 23, panel: 'hormones', date: '2026-02-15' },
  // Metabolic markers for metabolic health scoring
  { marker: 'HbA1c', value: 5.2, unit: '%', ref_low: 4.0, ref_high: 5.6, panel: 'metabolic', date: '2026-02-15' },
  { marker: 'Glucose', value: 85, unit: 'mg/dL', ref_low: 65, ref_high: 99, panel: 'metabolic', date: '2026-02-15' },
  { marker: 'HDL', value: 55, unit: 'mg/dL', ref_low: 40, ref_high: 100, panel: 'lipids', date: '2026-02-15' },
  { marker: 'Triglycerides', value: 95, unit: 'mg/dL', ref_low: 0, ref_high: 150, panel: 'lipids', date: '2026-02-15' },
];

// ─── Phenotype input (produces HPA-Dominant primary) ────────────────────────

export const DEMO_PHENOTYPE_INPUT: PhenotypeInput = {
  phq4: { q1: DEMO_PHQ4_LATEST.q1, q2: DEMO_PHQ4_LATEST.q2, q3: DEMO_PHQ4_LATEST.q3, q4: DEMO_PHQ4_LATEST.q4 },
  pillars: { diet: 2, stress: 1, substances: 3, social: 2 },
  appetite: 2,
  weightDelta: -1.3,
  checkin: {
    rumScore: 5,        // moderate rumination → HPA signal
    badsAvoidance: 1,
    badsActivation: 3,
    hintScore: 3,
  },
  symptoms: ['Fatigue', 'Brain fog', 'Poor sleep'],
  labs: {
    hsCRP: 2.1,
    insulin: 9.5,
    cortisol: 19,
    vitaminD: 32,
    tsh: 2.3,
    b12: 650,
    ferritin: 45,
    omega3Index: 5.2,
  },
  bodyComposition: {
    bmi: 24.8,
    bodyFat: 22.3,
    vo2Max: 36.2,
    bodyFatDelta: -0.5,
    vo2MaxDelta: 0.8,
  },
  wearables: {
    avgHRV: 48,
    avgSleepScore: 74,
    avgStressScore: 35,
    avgReadiness: 72,
  },
};

export const DEMO_PHENOTYPE_PROFILE = gradePhenotypes(DEMO_PHENOTYPE_INPUT);

// ─── Lab map for Phenotype page ─────────────────────────────────────────────

export const DEMO_LAB_MAP: Record<string, { value: number; unit: string }> = {};
for (const lab of DEMO_KEY_LABS) {
  DEMO_LAB_MAP[lab.marker] = { value: lab.value, unit: lab.unit };
}

// ─── Per-date Apple Health maps (for Activity page) ─────────────────────────

function buildDateMaps() {
  const daylight: Record<string, number> = {};
  const rhr: Record<string, number> = {};
  const cal: Record<string, number> = {};
  const dist: Record<string, number> = {};
  for (const m of DEMO_METRICS) {
    daylight[m.date] = 25 + Math.round(Math.random() * 40);   // 25–65 min
    rhr[m.date] = 58 + Math.round(Math.random() * 10);        // 58–68 bpm
    cal[m.date] = 250 + Math.round(Math.random() * 300);      // 250–550 kcal
    dist[m.date] = Math.round((2 + Math.random() * 4) * 10) / 10; // 2.0–6.0 mi
  }
  return { daylight, rhr, cal, dist };
}

const _dateMaps = buildDateMaps();
export const DEMO_DAYLIGHT_BY_DATE = _dateMaps.daylight;
export const DEMO_RHR_BY_DATE = _dateMaps.rhr;
export const DEMO_CAL_BY_DATE = _dateMaps.cal;
export const DEMO_DIST_BY_DATE = _dateMaps.dist;

export const DEMO_ACTIVITY_EXTRAS = {
  restingHR: 62,
  spo2: 97,
  distance: 4.2,
  activeCalories: 385,
  weight: 172.5,
  daylight: 45,
};

// ─── Demo home-leaving data ──────────────────────────────────────────────────
export const DEMO_HOME_LEAVING = (() => {
  const days: { date: string; leftHome: boolean; awayMins: number }[] = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    // Deterministic pattern: home-bound on days 1-2 (recent streak) and day 15
    const leftHome = i === 2 || i === 1 ? false : i !== 15;
    const awayMins = leftHome ? 120 + ((i * 37) % 360) : 0;
    days.push({ date, leftHome, awayMins });
  }
  return days;
})();

// ─── Full LabEntry[] for Labs page (multiple dates per marker) ───────────────

function buildLabEntries(): LabEntry[] {
  const entries: LabEntry[] = [];
  const dates = ['2025-08-15', '2025-11-10', '2026-02-15'];
  let id = 0;

  const markers: { marker: string; panel: LabPanel; unit: string; ref_low: number; ref_high: number; values: number[] }[] = [
    // Metabolic
    { marker: 'Glucose', panel: 'metabolic', unit: 'mg/dL', ref_low: 65, ref_high: 99, values: [92, 88, 85] },
    { marker: 'HbA1c', panel: 'metabolic', unit: '%', ref_low: 4.0, ref_high: 5.6, values: [5.4, 5.3, 5.2] },
    { marker: 'Insulin', panel: 'metabolic', unit: 'uIU/mL', ref_low: 2, ref_high: 25, values: [12.5, 10.8, 9.5] },
    { marker: 'hsCRP', panel: 'metabolic', unit: 'mg/L', ref_low: 0, ref_high: 3, values: [3.2, 2.8, 2.1] },
    { marker: 'Magnesium', panel: 'metabolic', unit: 'mg/dL', ref_low: 1.7, ref_high: 2.4, values: [1.9, 2.0, 2.1] },
    // Lipids
    { marker: 'Total Cholesterol', panel: 'lipids', unit: 'mg/dL', ref_low: 100, ref_high: 200, values: [210, 195, 188] },
    { marker: 'LDL', panel: 'lipids', unit: 'mg/dL', ref_low: 0, ref_high: 100, values: [128, 112, 105] },
    { marker: 'HDL', panel: 'lipids', unit: 'mg/dL', ref_low: 40, ref_high: 100, values: [48, 52, 55] },
    { marker: 'Triglycerides', panel: 'lipids', unit: 'mg/dL', ref_low: 0, ref_high: 150, values: [145, 130, 118] },
    // Thyroid
    { marker: 'TSH', panel: 'thyroid', unit: 'mIU/L', ref_low: 0.4, ref_high: 4.0, values: [2.8, 2.5, 2.3] },
    { marker: 'Free T4', panel: 'thyroid', unit: 'ng/dL', ref_low: 0.8, ref_high: 1.8, values: [1.1, 1.2, 1.3] },
    // Hormones
    { marker: 'Cortisol', panel: 'hormones', unit: 'ug/dL', ref_low: 6, ref_high: 23, values: [22, 20, 19] },
    { marker: 'Testosterone', panel: 'hormones', unit: 'ng/dL', ref_low: 300, ref_high: 1000, values: [420, 450, 480] },
    // CBC
    { marker: 'Ferritin', panel: 'cbc', unit: 'ng/mL', ref_low: 15, ref_high: 200, values: [35, 40, 45] },
    { marker: 'Hemoglobin', panel: 'cbc', unit: 'g/dL', ref_low: 13.5, ref_high: 17.5, values: [14.8, 15.0, 15.2] },
    // Other
    { marker: 'Vitamin D', panel: 'other', unit: 'ng/mL', ref_low: 30, ref_high: 100, values: [24, 28, 32] },
    { marker: 'Vitamin B12', panel: 'other', unit: 'pg/mL', ref_low: 200, ref_high: 1100, values: [450, 550, 650] },
    { marker: 'Omega-3 Index', panel: 'other', unit: '%', ref_low: 4, ref_high: 12, values: [3.8, 4.5, 5.2] },
    { marker: 'Homocysteine', panel: 'other', unit: 'umol/L', ref_low: 4, ref_high: 15, values: [13, 12, 11] },
  ];

  for (const m of markers) {
    for (let i = 0; i < dates.length; i++) {
      entries.push({
        id: `demo-lab-${id++}`,
        user_id: DEMO_PROFILE.id,
        date: dates[i],
        panel: m.panel,
        marker: m.marker,
        value: m.values[i],
        unit: m.unit,
        ref_low: m.ref_low,
        ref_high: m.ref_high,
        note: null,
        created_at: dates[i] + 'T12:00:00Z',
      });
    }
  }
  return entries;
}

export const DEMO_LAB_ENTRIES: LabEntry[] = buildLabEntries();

// ─── Full DashboardData return ──────────────────────────────────────────────

export function getDemoDashboardData(refresh: () => void): DashboardData {
  const phq4 = DEMO_PHQ4_LATEST;
  return {
    metrics: DEMO_METRICS,
    loading: false,
    error: '',
    hasOuraData: true,
    appleExtras: DEMO_APPLE_EXTRAS,
    appleDailyMaps: { rhr: {}, spo2: {}, distance: {}, calories: {}, daylight: {}, weight: {} },
    keyLabs: DEMO_KEY_LABS,
    phq4Data: { q1: phq4.q1, q2: phq4.q2, q3: phq4.q3, q4: phq4.q4 },
    phq4Entries: DEMO_PHQ4_ENTRIES,
    phenoPrimary: DEMO_PHENOTYPE_PROFILE.primary
      ? { name: DEMO_PHENOTYPE_PROFILE.primary.name, score: DEMO_PHENOTYPE_PROFILE.primary.score, color: DEMO_PHENOTYPE_PROFILE.primary.color }
      : null,
    phenoPrimaryResult: DEMO_PHENOTYPE_PROFILE.primary ?? null,
    phq4ScreeningLevel: DEMO_PHENOTYPE_PROFILE.screeningLevel,
    weeklyCheckins: [DEMO_CHECKIN],
    homeLeaving: DEMO_HOME_LEAVING,
    userAge: 35,
    userSex: 'male' as const,
    refresh,
  };
}
